"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useEffect, useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import useEmblaCarousel from "embla-carousel-react"
import { contentFieldIsFilled as isFilled } from "../../src/components/content/contentRenderPrimitives"
import type {
  LinkField,
  SliceComponentProps,
} from "../../src/lib/content/legacyBlockTypes"
import { validateUrl } from "../../src/lib/url/validateUrl"
import * as Sentry from "@sentry/nextjs"

type SocialMediaCarouselProps = SliceComponentProps

type CarouselItem = {
  socialmedialink?: LinkField | null
  socialmediaimage?: { url?: string; alt?: string | null } | null
  textoverlay?: string | null
}

const SocialMediaCarousel = ({ slice }: SocialMediaCarouselProps) => {
  const [emblaRef] = useEmblaCarousel({
    skipSnaps: false,
    loop: false,
    slidesToScroll: 1,
    align: "start",
  })

  const carouselArray: CarouselItem[] = useMemo(
    () => slice?.primary?.carouselimages || [],
    [slice],
  )

  useEffect(() => {
    carouselArray.forEach((mediaClipObj, index) => {
      const socialMediaLink = isFilled.link(mediaClipObj?.socialmedialink)
        ? mediaClipObj.socialmedialink.url
        : undefined
      if (socialMediaLink) {
        const validation = validateUrl(socialMediaLink)
        if (!validation.valid || validation.warnings.length > 0) {
          const errorMessage = `Malformed social media URL in SocialMediaCarousel slice: "${socialMediaLink}"`
          console.error(errorMessage, validation)
          Sentry.captureException(new Error(errorMessage), {
            level: validation.valid ? "warning" : "error",
            tags: {
              component: "SocialMediaCarousel",
              url_type: "social_media_link",
            },
            extra: {
              url: socialMediaLink,
              carouselIndex: index,
              validation,
            },
          })
        }
      }
    })
  }, [carouselArray])

  const renderIndividualImageTextAndLink = ({
    mediaClipObj,
    index,
  }: {
    mediaClipObj: CarouselItem
    index: number
  }) => {
    const socialMediaLink = isFilled.link(mediaClipObj?.socialmedialink)
      ? mediaClipObj.socialmedialink.url
      : undefined
    const textoverlay = mediaClipObj?.textoverlay

    if (!socialMediaLink) {
      return (
        <div className="mr-[36px] relative shrink-0" key={`media-clip-${index}`}>
          <Image
            width={239}
            height={285}
            src={mediaClipObj?.socialmediaimage?.url}
            alt={mediaClipObj?.socialmediaimage?.alt ?? ""}
            className="object-cover object-center border-solid border-black border-[3px] rounded-[15px] overflow-hidden"
          />
          <div
            className={
              "absolute top-[182px] left-[5px] right-[5px] bg-[#e6ff63] rounded-[10px] opacity-[0.81] px-[12px] mx-[3px]"
            }
          >
            <div
              className={
                "py-[8px] font-medium text-[20px] tracking-[3%] text-black leading-[24px]"
              }
            >
              {textoverlay}
            </div>
          </div>
        </div>
      )
    }

    return (
      <Link
        href={socialMediaLink}
        className="mr-[36px] relative shrink-0"
        key={`media-clip-${index}`}
        target="_blank"
      >
        <Image
          width={239}
          height={285}
          src={mediaClipObj?.socialmediaimage?.url}
          alt={mediaClipObj?.socialmediaimage?.alt ?? ""}
          className="object-cover object-center border-solid border-black border-[3px] rounded-[15px] overflow-hidden hover:drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
        />
        <div
          className={
            "absolute top-[182px] left-[5px] right-[5px] bg-[#e6ff63] rounded-[10px] opacity-[0.81] px-[12px] mx-[3px]"
          }
        >
          <div
            className={
              "py-[8px] font-medium text-[20px] tracking-[3%] text-black leading-[24px]"
            }
          >
            {textoverlay}
          </div>
        </div>
      </Link>
    )
  }
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full"
    >
      <div className="mt-[20px] mb-[40px] overflow-x-auto" ref={emblaRef}>
        <div className="flex flex-nowrap gap-0">
          {carouselArray.map((mediaClipObj, index) => {
            return renderIndividualImageTextAndLink({ mediaClipObj, index })
          })}
        </div>
      </div>
    </section>
  )
}

export default SocialMediaCarousel
